/**
 * MOCK service of the member detail tabs Roles, Groups, Resources and Security (People & Access).
 * contract TBD (ARP/backend, ARP-1480): endpoints and shapes are guesses behind the envelope
 * `{ status_code, body, meta }`. It reads and writes the same mock store as the other services, so a
 * role assigned here shows up in the Roles page and in the Members list.
 */
import { USER_TYPE } from '../domains/people-access/constants/copy.js';
import { envelope, errorEnvelope, notFoundEnvelope } from '../shared/utils/mockServiceHelpers.js';
import { notifyMembersChanged } from './membersService.js';
import { withMockQa } from './mockQaSwitches.js';
import {
  SEED_ROLE_ASSIGNED_BY,
  SEED_ROLE_ASSIGNMENT_DAYS,
  sessionDeviceFor,
} from './seeds/memberAccess.js';
import { store } from './seeds/store.js';

const DAY = 86_400_000;
/** Same window as the Remove flow: a member seen in the last 7 days has an active session. */
const ACTIVE_SESSION_WINDOW_DAYS = 7;

const findMember = (id) => store.members.find((m) => m.id === id);
const memberNotFound = () => notFoundEnvelope('Member not found');
const removedMember = () =>
  errorEnvelope(409, 'INVALID_STATE', 'A removed member has no access to manage.');

const privilegedRoleError = () =>
  errorEnvelope(409, 'PRIVILEGED_MEMBER', 'Admins and Owners get every permission by their user type.');

/** Looks the member up and refuses a removed one (they keep no roles, groups or resources). */
const editable = (memberId) => {
  const member = findMember(memberId);
  if (!member) return { error: memberNotFound() };
  if (member.status === 'removed') return { error: removedMember() };
  return { member };
};

/* ------------------------------------------------------------------------------------------ */
/* Roles                                                                                       */
/* ------------------------------------------------------------------------------------------ */

const assignmentOf = (memberId, roleId) =>
  store.roleAssignments[memberId]?.[roleId] ?? {
    assigned_by: SEED_ROLE_ASSIGNED_BY,
    assigned_at: new Date(Date.now() - SEED_ROLE_ASSIGNMENT_DAYS * DAY).toISOString(),
  };

/**
 * Roles of a member with who assigned them and when.
 * TODO(CT-5611 / backend): `GET /people-access/members/:id/roles` — contract TBD (ARP/backend).
 */
export const getMemberRoles = (memberId) => {
  const member = findMember(memberId);
  if (!member) return Promise.resolve(memberNotFound());
  const items = member.role_ids
    .map((id) => store.roles.find((r) => r.id === id))
    .filter(Boolean)
    .map((role) => ({
      id: role.id,
      name: role.name,
      description: role.description,
      type: role.type,
      permission_count: role.permissions.length,
      ...assignmentOf(memberId, role.id),
    }));
  return Promise.resolve(envelope({ items, total: items.length }));
};

/**
 * Give a member a role they do not have yet.
 * TODO(CT-5611 / backend): `POST /people-access/members/:id/roles` — contract TBD (ARP/backend).
 */
export const assignMemberRole = (memberId, roleId) =>
  withMockQa('roles', () => {
    const { member, error } = editable(memberId);
    if (error) return Promise.resolve(error);
    if (member.user_type !== USER_TYPE.MEMBER) return Promise.resolve(privilegedRoleError());
    const role = store.roles.find((r) => r.id === roleId);
    if (!role) return Promise.resolve(notFoundEnvelope('Role not found'));
    if (member.role_ids.includes(roleId)) {
      return Promise.resolve(errorEnvelope(409, 'ALREADY_ASSIGNED', 'The member already has this role.'));
    }
    store.members = store.members.map((m) =>
      m.id === memberId ? { ...m, role_ids: [...m.role_ids, roleId] } : m
    );
    store.roleAssignments[memberId] = {
      ...store.roleAssignments[memberId],
      [roleId]: { assigned_by: SEED_ROLE_ASSIGNED_BY, assigned_at: new Date().toISOString() },
    };
    notifyMembersChanged();
    return Promise.resolve(envelope({ member_id: memberId, role_id: roleId }));
  });

/**
 * Take a role away from a member. Direct studio grants are not touched.
 * TODO(CT-5611 / backend): `DELETE /people-access/members/:id/roles/:role_id` — contract TBD (ARP/backend).
 */
export const removeMemberRole = (memberId, roleId) =>
  withMockQa('roles', () => {
    const { member, error } = editable(memberId);
    if (error) return Promise.resolve(error);
    if (member.user_type !== USER_TYPE.MEMBER) return Promise.resolve(privilegedRoleError());
    if (!member.role_ids.includes(roleId)) {
      return Promise.resolve(notFoundEnvelope('The member does not have this role'));
    }
    store.members = store.members.map((m) =>
      m.id === memberId ? { ...m, role_ids: m.role_ids.filter((id) => id !== roleId) } : m
    );
    if (store.roleAssignments[memberId]) delete store.roleAssignments[memberId][roleId];
    notifyMembersChanged();
    return Promise.resolve(envelope({ member_id: memberId, role_id: roleId }));
  });

/* ------------------------------------------------------------------------------------------ */
/* Groups                                                                                      */
/* ------------------------------------------------------------------------------------------ */

const memberCountOf = (groupId) =>
  store.members.filter((m) => m.status !== 'removed' && m.group_ids.includes(groupId)).length;

/**
 * Groups a member belongs to, in the catalog order, with each group's member count.
 * TODO(CT-5611 / backend): `GET /people-access/members/:id/groups` — contract TBD (ARP/backend).
 */
export const getMemberGroups = (memberId) => {
  const member = findMember(memberId);
  if (!member) return Promise.resolve(memberNotFound());
  const items = store.groups
    .filter((g) => member.group_ids.includes(g.id))
    .map((g) => ({ id: g.id, name: g.name, description: g.description ?? '', member_count: memberCountOf(g.id) }));
  return Promise.resolve(envelope({ items, total: items.length }));
};

/**
 * Add a member to a group. Groups organize people; no access changes.
 * TODO(CT-5611 / backend): `POST /people-access/members/:id/groups` — contract TBD (ARP/backend).
 */
export const assignMemberGroup = (memberId, groupId) =>
  withMockQa('groups', () => {
    const { member, error } = editable(memberId);
    if (error) return Promise.resolve(error);
    if (!store.groups.some((g) => g.id === groupId)) {
      return Promise.resolve(notFoundEnvelope('Group not found'));
    }
    if (member.group_ids.includes(groupId)) {
      return Promise.resolve(errorEnvelope(409, 'ALREADY_ASSIGNED', 'The member is already in this group.'));
    }
    store.members = store.members.map((m) =>
      m.id === memberId ? { ...m, group_ids: [...m.group_ids, groupId] } : m
    );
    notifyMembersChanged();
    return Promise.resolve(envelope({ member_id: memberId, group_id: groupId, member_count: memberCountOf(groupId) }));
  });

/**
 * Take a member out of a group.
 * TODO(CT-5611 / backend): `DELETE /people-access/members/:id/groups/:group_id` — contract TBD (ARP/backend).
 */
export const removeMemberGroup = (memberId, groupId) =>
  withMockQa('groups', () => {
    const { member, error } = editable(memberId);
    if (error) return Promise.resolve(error);
    if (!member.group_ids.includes(groupId)) {
      return Promise.resolve(notFoundEnvelope('The member is not in this group'));
    }
    store.members = store.members.map((m) =>
      m.id === memberId ? { ...m, group_ids: m.group_ids.filter((id) => id !== groupId) } : m
    );
    notifyMembersChanged();
    return Promise.resolve(envelope({ member_id: memberId, group_id: groupId, member_count: memberCountOf(groupId) }));
  });

/* ------------------------------------------------------------------------------------------ */
/* Resources                                                                                   */
/* ------------------------------------------------------------------------------------------ */

const resourceRows = (memberId) => store.memberResources[memberId] ?? [];
/** The same resource reached by another path: same type and name. */
const sameResource = (a, b) => a.type === b.type && a.name === b.name;

/**
 * Resources a member reaches (datasets, models, event buses, sandboxes), one row per access
 * path, with how many other paths reach the same resource.
 * TODO(CT-5611 / backend): `GET /people-access/members/:id/resources` — contract TBD (ARP/backend).
 */
export const getMemberResources = (memberId) => {
  const member = findMember(memberId);
  if (!member) return Promise.resolve(memberNotFound());
  const rows = resourceRows(memberId);
  const items = rows
    .map((row) => ({
      ...row,
      other_paths: rows.filter((other) => sameResource(other, row) && other.id !== row.id).length,
    }))
    .sort((a, b) => a.name.localeCompare(b.name) || a.source.localeCompare(b.source));
  return Promise.resolve(envelope({ items, total: items.length }));
};

/**
 * Remove the DIRECT access of a member to a resource. Access that comes from a role or a group is
 * changed there, not here.
 * TODO(CT-5611 / backend): `DELETE /people-access/members/:id/resources/:resource_id` — contract TBD (ARP/backend).
 */
export const removeMemberResourceAccess = (memberId, resourceId) =>
  withMockQa('resources', () => {
    const { error } = editable(memberId);
    if (error) return Promise.resolve(error);
    const row = resourceRows(memberId).find((r) => r.id === resourceId);
    if (!row) return Promise.resolve(notFoundEnvelope('Resource access not found'));
    if (row.source !== 'direct') {
      return Promise.resolve(
        errorEnvelope(409, 'NOT_DIRECT', 'This access comes from a role or a group. Change it there.')
      );
    }
    store.memberResources[memberId] = resourceRows(memberId).filter((r) => r.id !== resourceId);
    const remaining = store.memberResources[memberId].filter((r) => sameResource(r, row)).length;
    return Promise.resolve(envelope({ id: resourceId, name: row.name, other_paths: remaining }));
  });

/* ------------------------------------------------------------------------------------------ */
/* Security                                                                                    */
/* ------------------------------------------------------------------------------------------ */

const hasActiveSession = (member) =>
  member.status !== 'removed' &&
  member.last_active_at != null &&
  (Date.now() - new Date(member.last_active_at).getTime()) / DAY <= ACTIVE_SESSION_WINDOW_DAYS;

/**
 * MFA enrollment and active sessions of a member. A member seen in the last 7 days has one active
 * session (the same rule the Remove flow uses for "active sessions").
 * TODO(CT-5611 / backend): `GET /people-access/members/:id/security` — contract TBD (ARP/backend).
 */
export const getMemberSecurity = (memberId) => {
  const member = findMember(memberId);
  if (!member) return Promise.resolve(memberNotFound());
  const sessions = hasActiveSession(member)
    ? [
        {
          id: `ses-${member.id}`,
          ...sessionDeviceFor(member.id),
          last_seen_at: member.last_active_at,
          current: true,
        },
      ]
    : [];
  return Promise.resolve(
    envelope({
      mfa: {
        enabled: Boolean(member.mfa_enabled),
        method: member.mfa_enabled ? 'authenticator_app' : null,
        enrolled_at: member.mfa_enabled ? member.added_at : null,
      },
      sessions,
    })
  );
};

/**
 * Ask a member who has not enrolled in MFA to do it (a reminder e-mail).
 * TODO(CT-5611 / backend): `POST /people-access/members/:id/mfa-reminder` — contract TBD (ARP/backend).
 */
export const sendMfaEnrollmentReminder = (memberId) =>
  withMockQa('security', () => {
    const member = findMember(memberId);
    if (!member) return Promise.resolve(memberNotFound());
    if (member.status !== 'active') {
      return Promise.resolve(
        errorEnvelope(409, 'INVALID_STATE', `Cannot remind a member who is ${member.status}.`)
      );
    }
    if (member.mfa_enabled) {
      return Promise.resolve(errorEnvelope(409, 'ALREADY_ENROLLED', 'The member already uses MFA.'));
    }
    return Promise.resolve(envelope({ member_id: memberId, email: member.email }));
  });
